import { Routes, Route, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { LandingLayout } from './components/layout/LandingLayout'
import { GameLayout } from './components/layout/GameLayout'
import Landing from './pages/Landing'
import StoryDetail from './pages/StoryDetail'
import EmailCapture from './pages/EmailCapture'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Claim from './pages/Claim'
import GamePlayer from './pages/GamePlayer'
import Profile from './pages/Profile'
import Settings from './pages/Settings'
import AnalysisPage from './pages/AnalysisPage'
import SessionSummary from './pages/SessionSummary'
import UserProfileAnalysis from './pages/UserProfileAnalysis'
import ProjectedProfile from './pages/ProjectedProfile'
import EnrollmentPage from './pages/EnrollmentPage'
import EnrollmentSuccess from './pages/EnrollmentSuccess'
import Teams from './pages/Teams'
import TeamCreate from './pages/TeamCreate'
import TeamDashboard from './pages/TeamDashboard'
import TeamAnalysis from './pages/TeamAnalysis'
import TeamJoin from './pages/TeamJoin'
import FacilitatorDashboard from './pages/FacilitatorDashboard'
import FacilitatorGroup from './pages/FacilitatorGroup'
import VizDemo from './pages/VizDemo'
import NotFound from './pages/NotFound'
import { useAtomValue } from 'jotai'
import { facilitatedGroupsAtom } from './atoms/authAtoms'

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes)

// The old story-keyed team route is gone; send facilitators to their group
// surface (team/member/role management now lives at the group altitude).
function FacilitateTeamsRedirect() {
  const groups = useAtomValue(facilitatedGroupsAtom)
  const target = groups[0]
  return <Navigate to={target ? `/facilitate/group/${target.id}` : '/'} replace />
}

export function AppRouter() {
  return (
    <SentryRoutes>
      {/* Public routes with landing layout */}
      <Route element={<LandingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/stories/:id" element={<StoryDetail />} />
        <Route path="/start/:id" element={<EmailCapture />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/claim/:token" element={<Claim />} />
        <Route path="/enroll/success" element={<EnrollmentSuccess />} />
        <Route path="/enroll/:groupName" element={<EnrollmentPage />} />
        <Route path="/team/:code" element={<TeamJoin />} />
        {/* Public dev gallery: preview/compare speech-visualizer styles. */}
        <Route path="/viz-demo" element={<VizDemo />} />
        <Route path="/f3/demo" element={<Navigate to="/start/144e1a85-b0fd-4f04-80df-272f98039810" replace />} />
        <Route path="/f3" element={<Navigate to="/stories/144e1a85-b0fd-4f04-80df-272f98039810" replace />} />
      </Route>

      {/* Protected game route */}
      <Route element={<GameLayout />}>
        <Route
          path="/play/:storyId"
          element={
            <ProtectedRoute>
              <GamePlayer />
            </ProtectedRoute>
          }
        />
        {/* Read-only walk-through of a completed session — same player chrome
            (GameLayout + GameScreen2), driven by session id instead of story
            id so it never creates/reopens a session. */}
        <Route
          path="/review/:sessionId"
          element={
            <ProtectedRoute>
              <GamePlayer />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Protected account routes */}
      <Route element={<LandingLayout />}>
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analysis/:sessionId"
          element={
            <ProtectedRoute>
              <AnalysisPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/summary/:sessionId"
          element={
            <ProtectedRoute>
              <SessionSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/analysis"
          element={
            <ProtectedRoute>
              <UserProfileAnalysis />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/projected/:sessionId"
          element={
            <ProtectedRoute>
              <ProjectedProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams/new"
          element={
            <ProtectedRoute>
              <TeamCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams/:id"
          element={
            <ProtectedRoute>
              <TeamDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams/:id/analysis/:teamStoryId"
          element={
            <ProtectedRoute>
              <TeamAnalysis />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facilitate/group/:groupId"
          element={
            <ProtectedRoute>
              <FacilitatorGroup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facilitate/:storyId"
          element={
            <ProtectedRoute>
              <FacilitatorDashboard />
            </ProtectedRoute>
          }
        />
        {/* Legacy story-keyed team-management route → group surface */}
        <Route
          path="/facilitate/:storyId/teams"
          element={
            <ProtectedRoute>
              <FacilitateTeamsRedirect />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </SentryRoutes>
  )
}
